import { PASSWORD_MIN_LENGTH } from '@erp/shared';
import { useEffect, useState } from 'react';

import { cn } from '../../lib/cn.ts';
import { loadStrengthChecker, STRENGTH_LABELS, type Strength } from '../../lib/strength.ts';

const COLORS = ['bg-danger', 'bg-danger', 'bg-warning', 'bg-success', 'bg-success'];

/**
 * Live guidance while choosing a password: the rules and a strength meter with
 * suggestions in Spanish. `serverMessage` is the API's verdict (e.g. leaked).
 */
export function PasswordStrength({
  password,
  userInputs,
  serverMessage,
  id,
}: {
  password: string;
  userInputs: string[];
  serverMessage?: string | null;
  id?: string;
}) {
  // The estimate belongs to the password it was computed for (no stale results).
  const [result, setResult] = useState<{ password: string; strength: Strength } | null>(null);

  useEffect(() => {
    if (!password) return;
    let cancelled = false;
    void loadStrengthChecker().then((check) => {
      if (!cancelled) setResult({ password, strength: check(password, userInputs) });
    });
    return () => {
      cancelled = true;
    };
  }, [password, userInputs]);

  const strength = password && result?.password === password ? result.strength : null;
  const longEnough = Array.from(password).length >= PASSWORD_MIN_LENGTH;
  return (
    <div id={id} className="space-y-2 text-sm">
      <ul className="space-y-0.5 text-muted-foreground">
        <li className={cn(longEnough && 'text-success')}>
          {longEnough ? '✓' : '•'} Al menos {PASSWORD_MIN_LENGTH} caracteres (una frase es más fácil
          de recordar)
        </li>
        <li>• Sin tu nombre, tu correo ni el nombre de la inmobiliaria</li>
      </ul>
      {strength && (
        <div aria-live="polite" className="space-y-1">
          <div className="flex gap-1" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn(
                  'h-1.5 flex-1 rounded-full bg-muted',
                  i < Math.max(1, strength.score) && COLORS[strength.score],
                )}
              />
            ))}
          </div>
          <p>
            Fortaleza: <strong>{STRENGTH_LABELS[strength.score]}</strong>
            {strength.warning ? ` — ${strength.warning}` : ''}
          </p>
          {strength.score < 3 && strength.suggestions[0] && (
            <p className="text-muted-foreground">{strength.suggestions[0]}</p>
          )}
        </div>
      )}
      {serverMessage && (
        <p role="alert" className="text-danger">
          {serverMessage}
        </p>
      )}
    </div>
  );
}
